import queryString from 'query-string';
import config from 'lib/config';
import { omit } from 'lodash';
import { SearchURL } from 'types/Apis/SearchURL';
import Comparison from 'types/Apis/Comparison';
import UserApi from 'types/Apis/UserApi';
import VehicleApiResponse from 'types/Apis/VehicleApiResponse';
import SimilarListing, { ResponseRecord } from 'types/Apis/SearchResponse';
import { AutolistUser } from 'types/User';
import VehicleWithExperience from 'types/Apis/VehicleWithExperience';
import {
  SearchParamsV2,
  SearchResultsV2ResponseData,
  VehicleWithExperienceApiResponse,
  SearchV2Result,
} from 'lib/AutolistAPI/types';
import ContentLocation from '../ContentLocation';
import getAuthToken from '../getAuthToken';
import { AdInfo } from '../CarGurusAds';
import { transformSrpResult, transformSrpVehicle } from './transformers';

// legacy types

// new types

export * from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JSON = Record<string, any>;

function errorWhenStatusCodeNot(response: Response, ...statusCodes: number[]): Response {
  if (!statusCodes.includes(response.status)) {
    throw new Error(`${response.status} fetching ${response.url}`);
  }

  return response;
}

function withAssertSuccess(baseFetch: typeof fetch = fetch): typeof fetch {
  return async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    const response = await baseFetch(input, init);
    if (!response.ok) {
      throw new Error(`Got status ${response.status} while fetching ${response.url}`);
    }
    return response;
  };
}

// url params is a function so that we can call it at runtime, because things like remote config
// need to be initialized first, which may not have happened when this file is imported,
// and seems to be out of our control
function withBaseUrl(url: () => string, baseFetch: typeof fetch = fetch): typeof fetch {
  return (input: RequestInfo, init?: RequestInit): ReturnType<typeof fetch> => {
    let newInput: RequestInfo;
    if (typeof input === 'string') {
      newInput = `${url()}${input}`;
    } else {
      newInput = Object.assign({}, input, { url: `${url()}${input.url}` });
    }
    return baseFetch(newInput, init);
  };
}

export function withAuth(baseFetch: typeof fetch = fetch): typeof fetch {
  return async (input: RequestInfo, init: RequestInit = {}): Promise<Response> => {
    const headers = { ...(init.headers || {}), 'x-campaigns': await getAuthToken() };

    return baseFetch(input, { ...init, headers });
  };
}

export function withOptionalAuth(baseFetch: typeof fetch = fetch): typeof fetch {
  return async (input: RequestInfo, init: RequestInit = {}): Promise<Response> => {
    let headers = init.headers || {};

    try {
      const authToken = await getAuthToken();
      headers = { ...headers, 'x-campaigns': authToken };
    } catch (e) {
      console.warn(`unable to get auth token when fetching ${JSON.stringify(input)}: ${e}`);
    }

    return baseFetch(input, { ...init, headers });
  };
}

function withCache(cache: boolean) {
  return function withCacheFetch(baseFetch: typeof fetch = fetch): typeof fetch {
    return async (input: RequestInfo, init: RequestInit = {}): Promise<Response> => {
      let headers;

      if (cache === false) {
        headers = { ...(init.headers || {}), 'cache-control': 'no-cache ' };
      } else {
        ({ headers } = init);
      }

      return baseFetch(input, { ...init, headers });
    };
  };
}

type JSONFetchFn = (...params: Parameters<typeof fetch>) => Promise<any>;
function withJsonParsing(baseFetch: typeof fetch): JSONFetchFn {
  return async (input: RequestInfo, init?: RequestInit): Promise<any> => {
    const r = await baseFetch(input, init);
    return r.json();
  };
}

export const autolistFetch = withBaseUrl(() => config.coreApiUrl, fetch);
export const defaultAutolistFetch = withJsonParsing(withAssertSuccess(autolistFetch));
const lookupFetch = withBaseUrl(() => config.lookupApiUrl, fetch);
const defaultLookupFetch = withJsonParsing(withAssertSuccess(lookupFetch));

export async function fetchVehicleWithExperience(params: {
  vin: string;
  latitude?: number;
  longitude?: number;
  radius?: number;
  locale: string;
}): Promise<VehicleWithExperience> {
  const urlSearchParams = queryString.stringify({
    exp_lat: params.latitude,
    exp_lon: params.longitude,
    exp_radius: params.radius,
    exp_locale: params.locale,
    vin: params.vin,
  });
  return (await defaultAutolistFetch(
    `/api/vehicle_with_experience?${urlSearchParams}`
  )) as VehicleWithExperience;
}

export async function fetchSearchUrl(slug: string, params = {}): Promise<SearchURL | undefined> {
  const query = new URLSearchParams(params).toString();
  const response = await autolistFetch(`/api/search_urls/${slug}?${query}`);

  if (response.status === 404) {
    return undefined;
  }

  if (response.status !== 200) {
    throw new Error(`Search URL API error: ${response.status} ${response.headers} ${response.url}`);
  }

  return response.json();
}

export type ListingsResponse = JSON;

export async function fetchListings(params: JSON): Promise<JSON> {
  const query = queryString.stringify(params);
  return defaultAutolistFetch(`/search?${query}`);
}

export async function fetchMakesAndModels(): Promise<JSON> {
  const headers = { 'Accept-Encoding': 'gzip, deflate' };
  return defaultLookupFetch('/makes', { headers });
}

export async function postLeadFeedback(leadId: number, params: JSON): Promise<JSON> {
  const query = new URLSearchParams(params);
  return defaultAutolistFetch(`/api/leads/${leadId}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: query.toString(),
  });
}

type AutocompReq = {
  query: string;
  limit: number;
};

export async function locationAutocomplete({
  query,
  limit = 5,
}: AutocompReq): Promise<ContentLocation[]> {
  const urlQuery = new URLSearchParams({
    q: query,
    limit: String(limit),
  }).toString();
  const results = await defaultLookupFetch(`/locations/content-autocomplete?${urlQuery}`);

  return results.map((result: JSON) => {
    return new ContentLocation(result as any);
  });
}

export async function fetchInfographics(): Promise<JSON> {
  return defaultLookupFetch('/infographics');
}

export async function fetchQuickPicks(vin: string): Promise<JSON> {
  return defaultAutolistFetch(`/api/vehicles/${vin}/quick_picks`);
}

export async function fetchArticles(
  params: {
    locale: string;
    parentCategory: string;
    abridged?: boolean;
  },
  options = {
    bypassCache: false,
  }
): Promise<JSON> {
  const url = new URL(`${config.contentApiUrl}/articles`);

  const fetcher = withCache(!options.bypassCache)(fetch);

  url.search = new URLSearchParams(params as JSON).toString();

  const response = errorWhenStatusCodeNot(await fetcher(url.toString()), 200);

  return response.json();
}

export async function fetchGuidesFeaturedArticles(
  params: {
    locale: string;
    abridged?: boolean;
  },
  options = {
    bypassCache: false,
  }
): Promise<JSON> {
  const url = new URL(`${config.contentApiUrl}/content-landing-pages/guides/featured-articles`);

  const fetcher = withCache(!options.bypassCache)(fetch);

  url.search = new URLSearchParams(params as JSON).toString();

  const response = errorWhenStatusCodeNot(await fetcher(url.toString()), 200);

  return response.json();
}

export async function fetchArticle(
  slug: string,
  params: {
    // eslint-disable-next-line camelcase
    parent_page?: string;
    related?: boolean;
    locale: string;
  },
  options = {
    bypassCache: false,
  }
): Promise<JSON | undefined> {
  const url = new URL(`${config.contentApiUrl}/articles`);

  const fetcher = withCache(!options.bypassCache)(fetch);

  url.search = new URLSearchParams(omit(params) as JSON).toString();
  url.pathname += `/${slug}`;

  const response = await fetcher(url.toString());

  if (response.status === 404) {
    return undefined;
  }

  if (response.status !== 200) {
    throw new Error(`Articles API error fetching ${url}`);
  }

  return response.json();
}

export function comparisonUrl(slug: string, params: { locale: string }): URL {
  const url = new URL(`${config.comparisonsApiUrl}/${slug}`);

  url.search = new URLSearchParams(params as JSON).toString();

  return url;
}

export async function fetchComparison(
  slug: string,
  params: {
    locale: string;
  },
  options = {
    bypassCache: false,
  }
): Promise<
  | {
      comparisons: Comparison[];
      comparison: Comparison;
    }
  | undefined
> {
  const url = comparisonUrl(slug, params);

  const fetcher = withCache(!options.bypassCache)(fetch);

  const response = await fetcher(url.toString());

  if (response.status === 404) {
    return undefined;
  }

  if (response.status !== 200) {
    throw new Error(`Comparisons API error fetching ${url}`);
  }

  return response.json();
}

export async function fetchAllComparisons(
  params: {
    locale: string;
    abridged?: boolean;
  },
  bypassCache: boolean
): Promise<{ comparisons: Comparison[] }> {
  const url = new URL(`${config.comparisonsApiUrl}`);

  url.search = new URLSearchParams(params as JSON).toString();

  const fetcher = withJsonParsing(withAssertSuccess(withCache(!bypassCache)(fetch)));

  const result = await fetcher(url.toString());

  return result || { comparisons: [] };
}

export async function fetchUser(): Promise<AutolistUser> {
  const url = new URL('/api/current_user', config.coreApiUrl);

  const response = await withAuth(fetch)(url.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const payload = (await response.json()) as UserApi;

  return {
    firstName: payload.first_name || undefined,
    lastName: payload.last_name || undefined,
    email: payload.email,
    phone: payload.phone || undefined,
    zip: payload.zip || undefined,
    loanDetails: {
      interestRate: payload.interest_rate ?? undefined,
      loanDuration: payload.loan_duration || undefined,
      downPayment: payload.down_payment ?? undefined,
      downPaymentPercentage: payload.down_payment_percentage ?? undefined,
      shouldUseDownPaymentPercentage: payload.use_down_payment_percentage ?? undefined,
    },
  };
}

export async function updateUser(params: Partial<UserApi>): Promise<void> {
  const url = new URL('/api/current_user', config.coreApiUrl);

  await withAuth(fetch)(url.toString(), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
}

export async function fetchSimilarListings(vin: string): Promise<ResponseRecord[]> {
  const response = (await defaultAutolistFetch(`/api/vehicles/${vin}/similar`)) as SimilarListing;
  return response.records;
}

export function fetchVehicle(vin: string): Promise<VehicleApiResponse> {
  return defaultAutolistFetch(`/api/vehicles/${vin}`);
}

export async function searchVehicles(params: SearchParamsV2): Promise<SearchV2Result> {
  const result: SearchResultsV2ResponseData = await defaultAutolistFetch(
    `/api/v2/search?${queryString.stringify(params || {})}`
  );

  const vins = result.records.map((record) => record.vin);
  if (typeof params?.latitude === 'number' && typeof params?.longitude === 'number') {
    const vehicleWithExperience = await fetchVehiclesWithExperience({
      vins,
      latitude: params?.latitude,
      longitude: params?.longitude,
      locale: params.locale,
    });

    return transformSrpResult(result, vehicleWithExperience);
  }
  return { vehicles: result.records.map((vehicle) => transformSrpVehicle(vehicle)) };
}

export interface FetchVehiclesWithExperienceParams {
  vins: string[];
  latitude?: number;
  longitude?: number;
  radius?: number;
  locale: string;
}

// bulk retrieval endpoint: https://www.pivotaltracker.com/n/projects/2406515/stories/180755662
export async function fetchVehiclesWithExperience(
  params: FetchVehiclesWithExperienceParams
): Promise<VehicleWithExperienceApiResponse[]> {
  const promises = params.vins.map((vin) => fetchVehicleWithExperience({ vin, ...params }));

  const vehiclesWithExperience: VehicleWithExperienceApiResponse[] = [];
  const results = await Promise.allSettled<VehicleWithExperienceApiResponse>(promises);
  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      vehiclesWithExperience.push(result.value);
    }
  });

  return vehiclesWithExperience;
}

export async function fetchAdInfo(vin: string): Promise<AdInfo | undefined> {
  const response = (await defaultAutolistFetch(
    `/api/vehicles/${vin}?ads=web`
  )) as VehicleApiResponse;
  return response.ad_info;
}
